/**
 * PIM — Offer configuration check.
 *
 * The products of the OPEN offers whose Compuzz configuration cannot honour
 * what the offer was built with: a print area or an imprint technology the
 * product does not carry, an ordered option with no Compuzz equivalent, or no
 * picture at all.
 *
 * A PIM offer keeps its own snapshot of the supplier product while production
 * works from the Compuzz product of the same code. When the two drift apart the
 * conversion silently drops what it cannot resolve — this screen is that drop
 * made visible, before the offer becomes an order.
 *
 * Grouped by product and not by offer: the fix is always made on the product,
 * and one broken print area usually shows up in several offers at once. The
 * offers are listed under their product so the sales side can see what is
 * blocked; a click opens the offer, a click on the code opens the PIM sheet.
 */
define('pimOfferConfigCheckView',[
  'require',
  'module',
  'app',
  'marionette',
  'underscore',
  'jquery',
  'settings'
], function (require, Module, App, Marionette, _, $, Settings) {
  'use strict';

  // Order matters: the chips are drawn in this order and a product's badges
  // follow it, so two cards never present the same problems differently.
  var ISSUES = [
    { key: 'PRODUCT_MISSING', label: 'No product', cls: 'rose' },
    { key: 'PIM_DATA_MISSING', label: 'No PIM data', cls: 'rose' },
    { key: 'PRINT_AREA_MISSING', label: 'Print area', cls: 'violet' },
    { key: 'IMPRINT_TECHNOLOGY_MISSING', label: 'Imprint tech.', cls: 'amber' },
    { key: 'PRINT_AREA_NOT_POSITIONED', label: 'PA not placed', cls: 'violet' },
    { key: 'OPTION_MISSING', label: 'Option', cls: 'blue' },
    { key: 'OPTION_CATEGORY_MISSING', label: 'Option category', cls: 'blue' },
    { key: 'VRP_ERROR', label: 'Render error', cls: 'rose' },
    { key: 'IMAGE_MISSING', label: 'No picture', cls: 'grey' },
    { key: 'CATALOG_PICTURE_MISSING', label: 'No catalogue picture', cls: 'grey' }
  ];

  // The statuses the scan runs on. Kept per browser: an account's offer
  // pipeline is a long one, and the statuses worth watching are a team habit,
  // not a per-visit decision.
  var STATUS_STORAGE_KEY = 'pim.offerConfigCheck.statuses';

  function loadStatuses() {
    try {
      var raw = window.localStorage ? window.localStorage.getItem(STATUS_STORAGE_KEY) : null;
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  }

  function saveStatuses(statuses) {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(STATUS_STORAGE_KEY, JSON.stringify(statuses || []));
      }
    } catch (e) { /* ignore quota / privacy-mode errors */ }
  }

  function issueDef(key) {
    return _.find(ISSUES, function (issue) { return issue.key === key; }) ||
      { key: key, label: key, cls: 'grey' };
  }

  function formatDate(value) {
    if (!value) { return ''; }
    var date = new Date(value);
    if (isNaN(date.getTime())) { return ''; }
    var day = ('0' + date.getDate()).slice(-2);
    var month = ('0' + (date.getMonth() + 1)).slice(-2);
    return day + '/' + month + '/' + date.getFullYear();
  }

  function formatAmount(value) {
    if (!value && value !== 0) { return ''; }
    return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' €';
  }

  function offerLabel(offer) {
    return _.compact([
      offer.customerName || offer.endUserId,
      offer.status,
      formatDate(offer.date)
    ]).join(' · ');
  }

  var CARD_TPL = _.template(
    '<section class="occ-card <%= open ? "open" : "" %>" data-code="<%= code %>">' +
    '  <header class="occ-head js-head" tabindex="0">' +
    '    <span class="occ-caret">▸</span>' +
    '    <span class="occ-id">' +
    '      <span class="occ-code js-pim" <% if (pimId) { %>data-pim="<%= pimId %>" title="Open the PIM sheet"<% } %>><%= code %></span>' +
    '      <span class="occ-name"><%= name %></span>' +
    '    </span>' +
    '    <span class="occ-sup"><%= supplier %></span>' +
    '    <span class="occ-badges"><%= badges %></span>' +
    '    <span class="occ-count"><%= offerCount %></span>' +
    '  </header>' +
    '  <div class="occ-drop">' +
    '    <ul class="occ-details"><%= details %></ul>' +
    '    <ul class="occ-offers"><%= offers %></ul>' +
    '  </div>' +
    '</section>'
  );

  var OFFER_TPL = _.template(
    '<li class="occ-offer js-offer" data-poi="<%= poi %>" tabindex="0">' +
    '  <span class="occ-poi"><%= poi %></span>' +
    '  <span class="occ-meta"><%= meta %></span>' +
    '  <span class="occ-amt"><%= price %></span>' +
    '</li>'
  );

  Module.exports = Marionette.ItemView.extend({
    className: 'pim-offer-config-check-view',

    template: _.template(
      '<div class="occ-bar">' +
      '  <button type="button" class="occ-tab js-filter on" data-filter="">All<span class="n"></span></button>' +
      '  <% _.each(issues, function (issue) { %>' +
      '  <button type="button" class="occ-tab quiet js-filter <%= issue.cls %>" data-filter="<%= issue.key %>">' +
      '    <%= issue.label %><span class="n"></span>' +
      '  </button>' +
      '  <% }); %>' +
      '  <span class="occ-grow"></span>' +
      '  <span class="occ-statuses js-statuses"></span>' +
      '  <span class="occ-find"><input type="text" class="js-search" placeholder="Product code, name, supplier"/></span>' +
      '  <button type="button" class="occ-reload js-refresh" title="Refresh">⟲</button>' +
      '</div>' +
      '<div class="occ-body js-body"></div>' +
      '<div class="occ-foot js-foot"></div>'
    ),

    ui: {
      body: '.js-body',
      foot: '.js-foot',
      search: '.js-search'
    },

    events: {
      'click .js-filter': 'onFilter',
      'click .js-refresh': 'onRefresh',
      'keyup .js-search': 'onSearchKey',
      'click .js-head': 'onHead',
      'keydown .js-head': 'onHeadKey',
      'click .js-offer': 'onOffer',
      'keydown .js-offer': 'onOfferKey'
    },

    initialize: function () {
      this.rows = [];
      this.filter = '';
      this.search = '';
      this.open = {};
      this.statuses = loadStatuses();
    },

    serializeData: function () {
      return { issues: ISSUES };
    },

    onShow: function () {
      this.renderStatusFilter();
      this.fetch();
    },

    onDestroy: function () {
      if (this.searchTimer) {
        window.clearTimeout(this.searchTimer);
      }
      if (this.statusDropdown) {
        this.statusDropdown.destroy();
        this.statusDropdown = null;
      }
    },

    /**
     * The status filter is the only one that travels to the database — the
     * chips and the search box work on what came back. Narrowing the statuses
     * is therefore the way to keep the scan cheap on a big pipeline.
     */
    renderStatusFilter: function () {
      var self = this;
      var $host = this.$('.js-statuses');
      if (!$host.length) { return; }
      require(['multiSelectDropdownView', 'entities/orders'], function (MultiSelectDropdown) {
        if (self.isDestroyed) { return; }
        self.statusDropdown = new MultiSelectDropdown({
          items: [],
          selection: self.statuses,
          placeholder: 'All open statuses',
          countLabel: 'statuses',
          searchPlaceholder: 'Find a status',
          onApply: function (picked) {
            self.statuses = (picked || []).slice();
            saveStatuses(self.statuses);
            self.fetch();
          }
        }).render();
        $host.append(self.statusDropdown.el);

        $.when(App.request('orders:statuses', 'offer')).done(function (statuses) {
          if (self.isDestroyed || !self.statusDropdown) { return; }
          self.statusDropdown.setItems(_.compact(_.map(statuses.models || statuses, function (status) {
            var code = status.get ? status.get('statusConst') : status.statusConst;
            var label = status.get ? status.get('status') : status.status;
            return code ? { value: code, label: label || code } : null;
          })));
        });
      });
    },

    // ---- data -----------------------------------------------------------
    fetch: function () {
      var self = this;
      this.ui.body.html('<div class="occ-state">Loading…</div>');
      this.ui.foot.empty();
      var params = {};
      if (this.statuses.length) { params.statuses = this.statuses.join(','); }
      $.ajax({
        type: 'GET',
        url: Settings.url('compuzz2', 'v2/pim/offer-config-check', params),
        success: function (data) {
          if (self.isDestroyed) { return; }
          self.rows = data || [];
          self.renderBody();
        },
        error: function () {
          if (self.isDestroyed) { return; }
          self.ui.body.html('<div class="occ-state bad">' +
            'Could not run the check. Refresh to try again.</div>');
          self.ui.foot.empty();
        }
      });
    },

    /** The rows left by the issue chip and the search box. */
    visibleRows: function () {
      var filter = this.filter;
      var search = this.search.toLowerCase();
      return _.filter(this.rows, function (row) {
        if (filter && !_.contains(row.issues || [], filter)) { return false; }
        if (!search) { return true; }
        var haystack = [
          row.productCode, row.productName,
          row.supplierProductCode, row.supplierName
        ].join(' ').toLowerCase();
        return haystack.indexOf(search) !== -1;
      });
    },

    // ---- rendering ------------------------------------------------------
    renderBody: function () {
      this.renderCounts();
      var rows = this.visibleRows();
      if (!rows.length) {
        this.ui.body.html('<div class="occ-state">' +
          '<span class="occ-state-icon">✓</span>' +
          '<span class="occ-state-t">Nothing to fix</span>' +
          '<span class="occ-state-s">Every open offer runs on a product that can honour it.</span>' +
          '</div>');
        this.ui.foot.empty();
        return;
      }
      this.ui.body.html(_.map(rows, this.cardHtml, this).join(''));
      this.renderFoot(rows);
    },

    /** Every chip carries the number of products it would leave on screen. */
    renderCounts: function () {
      this.$('.js-filter[data-filter=""] .n').text(this.rows.length || '');
      _.each(ISSUES, function (issue) {
        var count = _.filter(this.rows, function (row) {
          return _.contains(row.issues || [], issue.key);
        }).length;
        this.$('.js-filter[data-filter="' + issue.key + '"] .n').text(count || '');
      }, this);
    },

    renderFoot: function (rows) {
      var offers = _.reduce(rows, function (total, row) {
        return total + (row.offerCount || 0);
      }, 0);
      this.ui.foot.html(rows.length + ' product' + (rows.length > 1 ? 's' : '') +
        ' · ' + offers + ' open offer' + (offers > 1 ? 's' : ''));
    },

    cardHtml: function (row) {
      var badges = _.map(row.issues || [], function (key) {
        var def = issueDef(key);
        return '<span class="occ-badge ' + def.cls + '">' + _.escape(def.label) + '</span>';
      }).join('');
      var details = _.map(row.details || [], function (detail) {
        return '<li>' + _.escape(detail) + '</li>';
      }).join('');
      var offers = _.map(row.offers || [], function (offer) {
        return OFFER_TPL({
          poi: _.escape(offer.poi || ''),
          meta: _.escape(offerLabel(offer)),
          price: _.escape(formatAmount(offer.price))
        });
      }).join('');

      return CARD_TPL({
        code: _.escape(row.productCode || ''),
        name: _.escape(row.productName || ''),
        pimId: _.escape(row.pimProductId || ''),
        supplier: _.escape(_.compact([row.supplierName, row.supplierProductCode]).join(' · ')),
        badges: badges,
        details: details,
        offers: offers,
        offerCount: (row.offerCount || 0) + ' offer' + (row.offerCount > 1 ? 's' : ''),
        open: !!this.open[row.productCode]
      });
    },

    // ---- events ---------------------------------------------------------
    onFilter: function (e) {
      var $tab = $(e.currentTarget);
      this.filter = $tab.attr('data-filter') || '';
      this.$('.js-filter').removeClass('on');
      $tab.addClass('on');
      this.renderBody();
    },

    onRefresh: function () {
      this.fetch();
    },

    onSearchKey: function () {
      var self = this;
      if (this.searchTimer) { window.clearTimeout(this.searchTimer); }
      this.searchTimer = window.setTimeout(function () {
        if (self.isDestroyed) { return; }
        self.search = $.trim(self.ui.search.val() || '');
        self.renderBody();
      }, 250);
    },

    /**
     * The code opens the PIM sheet, the rest of the header folds the card open
     * on its offers.
     */
    onHead: function (e) {
      var pimId = $(e.target).closest('.js-pim').attr('data-pim');
      if (pimId) {
        App.navigate('pim/products/' + pimId, { trigger: true });
        return;
      }
      var $card = $(e.currentTarget).closest('.occ-card');
      var code = $card.attr('data-code');
      this.open[code] = !this.open[code];
      $card.toggleClass('open', !!this.open[code]);
    },

    onHeadKey: function (e) {
      if (e.keyCode !== 13 && e.keyCode !== 32) { return; }
      e.preventDefault();
      this.onHead(e);
    },

    // The offer opens in its own window: this screen is a place you come back
    // to, so nothing here navigates away from it.
    onOffer: function (e) {
      // attr() rather than data(): jQuery coerces data values, which would eat
      // the leading zeros of a numeric purchase order id.
      var poi = $(e.currentTarget).attr('data-poi');
      if (!poi) { return; }
      require(['ordersDetailView'], function (DetailView) {
        DetailView.prototype.open(poi);
      });
    },

    onOfferKey: function (e) {
      if (e.keyCode !== 13 && e.keyCode !== 32) { return; }
      e.preventDefault();
      this.onOffer(e);
    }
  });
});

